import React, { useState } from 'react';

import { LinkButton } from '@eika/button';
import { ExpandCard } from '@eika/expand-card';
import { IconTypes } from '@eika/icon';

import piwik from '@/piwik/piwik';

import useText from '@hooks/use-text';

import InstructionSteps from './instruction-steps/InstructionSteps';
import InstructionStepsNoIcon from './instruction-steps/InstructionStepsNoIcon';
import {
    getInstructionStepsPartFour,
    getInstructionStepsPartOne,
    getInstructionStepsPartThree,
    getInstructionStepsPartTwo,
} from './instruction-steps/steps';

import './GoogleWalletStepByStep.scss';

enum GoogleWalletStep {
    STEP_1,
    STEP_2,
    STEP_3,
    STEP_4,
}

export const GoogleWalletStepByStep = () => {
    const text = useText();

    const [expandedSteps, setExpandedSteps] = useState<GoogleWalletStep[]>([]);

    const {
        googleWalletInstructionsHeader,
        googleWalletInstructionStepsStep,
        googleWalletInstructionStepsPrivacyStep,
        googleWalletInstructionStepsPartOneSubtitle,
        googleWalletInstructionStepsPartTwoSubtitle,
        googleWalletInstructionStepsPartThreeSubtitle,
        googleWalletInstructionStepsPartFourSubtitle,
        googleWalletInstructionStepsPartFourStepThree,
    } = text;

    const isExpanded = (step: GoogleWalletStep) => expandedSteps.includes(step);

    const onChangeExpand = (expand: boolean, chosenStep: GoogleWalletStep) => {
        if (expand) {
            if (expandedSteps.some((step) => step === chosenStep)) {
                return;
            }

            setExpandedSteps([...expandedSteps, chosenStep]);
            return;
        }

        setExpandedSteps(expandedSteps.filter((step) => step !== chosenStep));
    };

    return (
        <section
            data-track-id={piwik.googlePay.stepByStep.dataTrackId}
            data-track-linkname={piwik.googlePay.stepByStep.dataTrackLinkname}
        >
            <h3 className="typography-subtitle-bold">{googleWalletInstructionsHeader}</h3>
            <div className="step-expand-cards">
                <ExpandCard
                    title={`${googleWalletInstructionStepsStep} 1`}
                    subtitle={googleWalletInstructionStepsPartOneSubtitle}
                    id="google-wallet-step-1"
                    isExpanded={isExpanded(GoogleWalletStep.STEP_1)}
                    setIsExpanded={(expand: boolean) => onChangeExpand(expand, GoogleWalletStep.STEP_1)}
                    dataTrackId={piwik.googlePay.stepByStep1.dataTrackId}
                    data-track-linkname={piwik.googlePay.stepByStep1.dataTrackLinkname}
                >
                    <InstructionSteps instructionSteps={getInstructionStepsPartOne(text)} />
                </ExpandCard>
                <ExpandCard
                    title={`${googleWalletInstructionStepsStep} 2`}
                    subtitle={googleWalletInstructionStepsPartTwoSubtitle}
                    id="google-wallet-step-2"
                    isExpanded={isExpanded(GoogleWalletStep.STEP_2)}
                    setIsExpanded={(expand: boolean) => onChangeExpand(expand, GoogleWalletStep.STEP_2)}
                    dataTrackId={piwik.googlePay.stepByStep2.dataTrackId}
                    data-track-linkname={piwik.googlePay.stepByStep2.dataTrackLinkname}
                >
                    <InstructionSteps instructionSteps={getInstructionStepsPartTwo(text)} />
                </ExpandCard>
                <ExpandCard
                    title={`${googleWalletInstructionStepsStep} 3`}
                    subtitle={googleWalletInstructionStepsPartThreeSubtitle}
                    id="google-wallet-step-3"
                    isExpanded={isExpanded(GoogleWalletStep.STEP_3)}
                    setIsExpanded={(expand: boolean) => onChangeExpand(expand, GoogleWalletStep.STEP_3)}
                    dataTrackId={piwik.googlePay.stepByStep3.dataTrackId}
                    data-track-linkname={piwik.googlePay.stepByStep3.dataTrackLinkname}
                >
                    <InstructionSteps instructionSteps={getInstructionStepsPartThree(text)} />
                </ExpandCard>
                <ExpandCard
                    title={googleWalletInstructionStepsPrivacyStep}
                    subtitle={googleWalletInstructionStepsPartFourSubtitle}
                    id="google-wallet-step-4"
                    isExpanded={isExpanded(GoogleWalletStep.STEP_4)}
                    setIsExpanded={(expand: boolean) => onChangeExpand(expand, GoogleWalletStep.STEP_4)}
                    dataTrackId={piwik.googlePay.stepByStep4.dataTrackId}
                    data-track-linkname={piwik.googlePay.stepByStep4.dataTrackLinkname}
                >
                    <InstructionStepsNoIcon instructionStepsNoIcon={getInstructionStepsPartFour(text)} />
                    <LinkButton
                        icon={IconTypes.PIL_HOEYRE}
                        iconOnRightSide
                        id="link-privacy"
                        onClick={() => {}}
                        data-track-id={piwik.googlePay.stepByStepPrivacyLink.dataTrackId}
                        data-track-linkname={piwik.googlePay.stepByStepPrivacyLink.dataTrackLinkname}
                    >
                        {googleWalletInstructionStepsPartFourStepThree}
                    </LinkButton>
                </ExpandCard>
            </div>
        </section>
    );
};
